import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listProjects, createProject } from '../lib/api'
import ModTextInputMinimal from '../ui/ModTextInputMinimal/ModTextInputMinimal'
import { useLang } from '../lib/useLang'
import './Projects.css'

const AVATAR_COLORS = [
  'linear-gradient(135deg, #FF5A1F, #C43D10)',
  'linear-gradient(135deg, #0E0E10, #3A3935)',
  'linear-gradient(135deg, #FF8453, #FF5A1F)',
  'linear-gradient(135deg, #3A3935, #0E0E10)',
  'linear-gradient(135deg, #FFB199, #FF5A1F)',
  'linear-gradient(135deg, #C43D10, #FF5A1F)',
]

function timeAgo(date, t) {
  const now = new Date()
  const d = new Date(date)
  const diff = Math.floor((now - d) / 1000)
  if (diff < 60) return t('projects.justNow')
  if (diff < 3600) return t('projects.minutesAgo', { n: Math.floor(diff / 60) })
  if (diff < 86400) return t('projects.hoursAgo', { n: Math.floor(diff / 3600) })
  if (diff < 2592000) return t('projects.daysAgo', { n: Math.floor(diff / 86400) })
  return d.toLocaleDateString()
}

export default function Projects() {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [err, setErr] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()
  const { t } = useLang()

  const load = async () => {
    setLoading(true)
    try {
      const rows = await listProjects()
      setItems(rows)
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('projects.failedLoad'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const onCreateFromModal = async () => {
    const n = (newName || '').trim()
    if (!n) return
    setErr('')
    try {
      const p = await createProject(n)
      setShowCreate(false)
      setNewName('')
      navigate(`/app/p/${p.id}`)
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('projects.failedCreate'))
    }
  }

  const filteredItems = items.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const gradientStyle = {
    background: `radial-gradient(ellipse 100% 60% at 50% 20%, rgba(255, 90, 31, 0.08) 0%, transparent 55%),
                 radial-gradient(ellipse 80% 50% at 50% 80%, rgba(14, 14, 16, 0.06) 0%, transparent 65%),
                 var(--cream, #F5F1E8)`,
  }

  return (
    <div
      className='projects-container relative min-h-screen overflow-hidden pt-24'
      style={gradientStyle}
    >
      {/* Soft accent orbs */}
      <div className='absolute top-1/4 left-1/4 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full' style={{ background: 'radial-gradient(circle, rgba(255, 90, 31, 0.10), transparent 70%)', filter: 'blur(40px)' }} />
      <div className='absolute bottom-1/4 right-1/4 h-[520px] w-[520px] translate-x-1/2 translate-y-1/2 rounded-full' style={{ background: 'radial-gradient(circle, rgba(14, 14, 16, 0.06), transparent 70%)', filter: 'blur(40px)' }} />

      {/* Grid pattern overlay */}
      <div className='absolute inset-0 bg-grid-pattern opacity-60' />

      <div className='relative z-10 mx-auto w-full max-w-7xl px-6 sm:px-10'>
        {/* Header */}
        <div className='mb-8 flex items-end justify-between'>
          <div>
            <h1 className='headline-display text-3xl font-light tracking-tight text-ink'>{t('projects.title')}</h1>
            <p className='mt-2 text-sm text-muted'>{t('projects.subtitle')}</p>
          </div>
          <div className='flex items-center gap-3'>
            <div className='relative'>
              <svg className='absolute left-3 top-1/2 -translate-y-1/2 text-muted' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <circle cx='11' cy='11' r='8' /><path d='m21 21-4.35-4.35' />
              </svg>
              <input
                type='text'
                placeholder={t('projects.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className='rounded pl-10 pr-4 py-2.5 text-sm outline-none transition-colors placeholder:text-ink/40 focus:bg-white'
                style={{ background: 'rgba(255, 255, 255, 0.55)', border: '1px solid rgba(14, 14, 16, 0.16)', color: '#0E0E10', width: '240px' }}
              />
            </div>
          </div>
        </div>

        {err && (
          <div className='mb-4 rounded border px-4 py-3 text-sm' style={{ borderColor: 'rgba(220, 38, 38, 0.25)', background: 'rgba(220, 38, 38, 0.08)', color: '#b9261c' }}>
            {err}
          </div>
        )}

        {loading ? (
          <div className='flex justify-center py-20'>
            <span className='loading loading-spinner loading-lg' />
          </div>
        ) : (
          <div className='projects-grid'>
            {/* New Project Card */}
            <button
              onClick={() => { setShowCreate(true); setNewName('') }}
              className='project-card project-card-new'
            >
              <div className='project-card-new-icon'>
                <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                  <line x1='12' y1='5' x2='12' y2='19' />
                  <line x1='5' y1='12' x2='19' y2='12' />
                </svg>
              </div>
              <span className='text-sm font-medium text-ink/70'>{t('projects.newProject')}</span>
            </button>

            {/* Project Cards */}
            {filteredItems.map((p, index) => (
              <div
                key={p.id}
                className='project-card'
                style={{ animationDelay: `${index * 0.04}s` }}
                onClick={() => navigate(`/app/p/${p.id}`)}
              >
                <div className='project-card-header'>
                  <div
                    className='project-card-avatar'
                    style={{ background: AVATAR_COLORS[p.id % AVATAR_COLORS.length] }}
                  >
                    {(p.name || '?')[0].toUpperCase()}
                  </div>
                  <svg className='project-card-arrow' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                    <path d='m9 18 6-6-6-6' />
                  </svg>
                </div>
                <div className='project-card-body'>
                  <h3 className='project-card-name'>{p.name}</h3>
                  <div className='project-card-meta'>
                    <span className='project-card-date'>
                      {t('projects.updated')} {timeAgo(p.updated_at || p.created_at, t)}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {filteredItems.length === 0 && !searchQuery && (
              <div className='project-card project-card-empty'>
                <div className='text-center'>
                  <div className='mb-3 text-ink/25'>
                    <svg width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1' className='mx-auto'>
                      <path d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' />
                    </svg>
                  </div>
                  <p className='text-sm text-muted'>{t('projects.noProjects')}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div
          className='projects-modal-overlay'
          onClick={() => setShowCreate(false)}
        >
          <div
            className='projects-modal'
            onClick={e => e.stopPropagation()}
          >
            <h2 className='projects-modal-title'>{t('projects.createProject')}</h2>
            <div className='projects-modal-content'>
              <ModTextInputMinimal
                label={t('projects.projectName')}
                autoFocus
                placeholder={t('projects.namePlaceholder')}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newName.trim()) onCreateFromModal()
                }}
              />
            </div>
            <div className='projects-modal-actions'>
              <button
                onClick={() => setShowCreate(false)}
                className='projects-modal-button-cancel'
              >
                {t('projects.cancel')}
              </button>
              <button
                onClick={onCreateFromModal}
                disabled={!newName.trim()}
                className='projects-modal-button-create'
              >
                {t('projects.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
